/* eslint-disable */
/**
 * POSProperties — Peace of Sleep Property Management
 *
 * Property-centric flat list:
 *  - Level 1: All properties in a flat DataTable
 *  - Level 2: Click row → full-page rooms view (PropertyRoomsView)
 *  - View / Edit Property → unified six-tab modal (PropertyDetailModal)
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDate } from '../../../../utils/formatters';
import { useAuth } from '../../../../contexts/AuthContext';
import { usePOS } from '../../../../contexts/POSContext';
import SearchSelect from '../../../../components/SearchSelect';
import { usePagePermission } from '../../../../hooks/usePagePermission';
import AccessDenied from '../../../../components/AccessDenied';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import DataTable, { StatusBadge, type Column, type HeaderActions } from '../../../../components/DataTable';

import {
  PERMISSIONS, STATUS_CONFIG, TierBadge, getDirectoryUrl, POS_GOLD, computeTemperature,
  TEMPERATURE_CONFIG, type Temperature
} from './constants';
import PropertyRoomsView from './PropertyRoomsView';
import PropertyDetailModal from './PropertyDetailModal';
import PortfolioBoard from './PortfolioBoard';
import RenewalsBoard from './RenewalsBoard';
import BoardToolbar from '../../../../components/BoardToolbar';

// Fields the board toolbar filters on (manager / ambassador / tier). Matched
// verbatim against the enriched property rows.
const BOARD_FILTER_KEYS = ['managerName', 'ambassadorName', 'hospitality_tier'] as const;
import type { PropertyMode, PropertyTab } from './property-detail/types';
import CertificatesModal from './CertificatesModal';
import BookingsModal from './BookingsModal';
import ServicesModal from './ServicesModal';

export default function POSProperties() {
  const { supabase, hasPermission } = useAuth();
  const {
    userProperties,
    propertiesLoading,
    isHotelManager,
    refreshProperties
  } = usePOS();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('pos_properties');
  const [searchParams, setSearchParams] = useSearchParams();

  // Permission checks. CMB staff at any scope (global or territory) follow
  // hasPermission level rules — contribute can create/edit, manage can
  // delete. Hotel managers (customer side) are gated by per-property
  // access_level instead, since they don't have feature-level POS perms.
  const canCreate = !isHotelManager && hasPermission('pos_properties', PERMISSIONS.CREATE);
  const canDeleteGlobal = !isHotelManager && hasPermission('pos_properties', PERMISSIONS.DELETE);
  // Board / toggle edit gate. View-level users reach the page (read) but must
  // not see the board action buttons or the publish/feature toggles.
  const canEditBoard = !isHotelManager && hasPermission('pos_properties', PERMISSIONS.EDIT);

  const canEditProp = (prop: Record<string, any> | null) => {
    if (!isHotelManager) return hasPermission('pos_properties', PERMISSIONS.EDIT);
    return ['owner', 'manager'].includes(prop?.access_level);
  };

  // Navigation state
  const [selectedProperty, setSelectedProperty] = useState<Record<string, any> | null>(null); // rooms page

  // Data state
  const [properties, setProperties] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Unified view/edit/create modal — handles all property interactions.
  // initialCustomerId pre-seeds the customer linkage when the modal is opened
  // from the orphan-POS-customer CTA on the Customers page.
  const [detailModal, setDetailModal] = useState<{ id: string | null; mode: PropertyMode; initialCustomerId?: string; initialTab?: import('./property-detail/types').PropertyTab } | null>(null);

  // Deep-link from "Create POS Property" CTA on a customer modal. Opens the
  // Add Property modal in create mode with the customer pre-linked, then
  // clears the param so refreshing the page doesn't reopen it.
  useEffect(() => {
    const createForCustomer = searchParams.get('create_for_customer');
    if (createForCustomer && canCreate) {
      setDetailModal({ id: null, mode: 'create', initialCustomerId: createForCustomer });
      const next = new URLSearchParams(searchParams);
      next.delete('create_for_customer');
      setSearchParams(next, { replace: true });
    }
  }, [canCreate, searchParams, setSearchParams]);

  // Rebuild state
  const [rebuilding, setRebuilding] = useState(false);

  // Portfolio tabs
  const [activeTab, setActiveTab] = useState('hospitality');

  // View mode toggle (board vs list). Board is the default on
  // 2026-05-29 — operators land on the task-centric view that surfaces
  // what's blocking each property. List is still available as the
  // canonical flat view of everything. Persisted to localStorage so
  // operator's choice sticks, matching Proposals + ServiceLogs pattern.
  const [viewMode, setViewMode] = useState<'list' | 'board' | 'renewals'>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('posPortfolioViewMode') : null;
    return saved === 'list' || saved === 'renewals' ? saved : 'board';
  });
  // Board (Onboarding + Renewals) toolbar state: search + filters + sort.
  const [boardSearch, setBoardSearch] = useState('');
  const [boardFilters, setBoardFilters] = useState<Record<string, string>>({});
  const [boardSort, setBoardSort] = useState('');
  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem('posPortfolioViewMode', viewMode);
  }, [viewMode]);

  // Helper that the board's task cards use to deep-link into the
  // unified Property modal. Passes initialTab through so e.g.
  // "Open Rooms" lands directly on the Rooms tab.
  const openPropertyFromBoard = (id: string, initialTab?: PropertyTab, mode: PropertyMode = 'view') => {
    setDetailModal({ id, mode, initialTab });
  };

  // View mode (list or board)

  // Memoised so the board (and its background-lookup effect) only receives a
  // new array when the property data actually changes. Without this the filter
  // ran on every render, handing the board a fresh array each time, which made
  // it refetch and visibly reset on any parent re-render, e.g. when returning
  // to the tab triggered a token refresh.
  const hospitalityProperties = useMemo(
    () => properties.filter(p => (p.property_category || 'hospitality') === 'hospitality'),
    [properties],
  );
  const communityProperties = useMemo(
    () => properties.filter(p => p.property_category === 'community-living'),
    [properties],
  );

  // Groups state
  const [groups, setGroups] = useState<Record<string, any>[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [viewingGroup, setViewingGroup] = useState<Record<string, any> | null>(null);
  const [editingGroup, setEditingGroup] = useState<Record<string, any> | null>(null);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);

  // Context modals
  const [certificatesContext, setCertificatesContext] = useState<Record<string, any> | null>(null);
  const [bookingsContext, setBookingsContext] = useState<Record<string, any> | null>(null);
  const [servicesContext, setServicesContext] = useState<Record<string, any> | null>(null);

  // Load enriched data when userProperties changes
  useEffect(() => {
    if (!propertiesLoading && userProperties.length > 0) {
      loadEnrichedData();
    } else if (!propertiesLoading && userProperties.length === 0) {
      setProperties([]);
      setLoading(false);
    }
  }, [userProperties, propertiesLoading]);

  // Auto-open property modal from query param (e.g. ?property=uuid)
  useEffect(() => {
    const propId = searchParams.get('property');
    if (propId && properties.length > 0 && !detailModal) {
      const match = properties.find(p => p.id === propId);
      if (match) {
        setDetailModal({ id: match.id, mode: 'view' });
        setSearchParams({}, { replace: true });
      }
    }
  }, [properties, searchParams]);

  // Sync selectedProperty when properties data refreshes
  useEffect(() => {
    if (selectedProperty) {
      const updated = properties.find(p => p.id === selectedProperty.id);
      if (updated && updated !== selectedProperty) {
        setSelectedProperty(updated);
      }
    }
  }, [properties]);

  const loadEnrichedData = async () => {
    if (userProperties.length === 0) {
      setProperties([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const propertyIds = userProperties.map(p => p.id);

      const { data, error: fetchError } = await supabase
        .from('hosp_properties')
        .select(`
          *,
          customer:customers(id, customer_name, company_name, email, phone),
          rooms:hosp_rooms(id),
          manager:users!hosp_properties_assigned_manager_id_fkey(first_name, last_name)
        `)
        .in('id', propertyIds)
        .order('property_name', { ascending: true })
        .limit(10000);

      if (fetchError) throw fetchError;

      // Fetch ambassador links — hosp_properties has ambassador_id directly
      const ambIds = [...new Set((data || []).map((p: Record<string, any>) => p.ambassador_id).filter(Boolean))];
      const ambMap: Record<string, { name: string | null; rate: number | null }> = {};
      if (ambIds.length > 0) {
        const { data: ambUsers } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .in('id', ambIds);
        (ambUsers || []).forEach((u: Record<string, any>) => {
          ambMap[u.id] = { name: `${u.first_name || ''} ${u.last_name || ''}`.trim(), rate: null };
        });
      }

      const enriched = (data || []).map(prop => {
        const contextProp = userProperties.find(p => p.id === prop.id);
        const amb = prop.ambassador_id ? ambMap[prop.ambassador_id] : null;
        return {
          ...prop,
          room_count: prop.rooms?.length || 0,
          access_level: contextProp?.access_level,
          customerName: prop.customer?.company_name || prop.customer?.customer_name || 'No customer linked',
          contractStatus: prop.contract_status || 'onboarding',
          managerName: prop.manager ? `${prop.manager.first_name || ''} ${prop.manager.last_name || ''}`.trim() : null,
          ambassadorName: amb?.name || null,
          ambassadorRate: amb?.rate || null,
          temperature: computeTemperature(prop.assured_until as string | null)
        };
      });

      setProperties(enriched);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load groups with their properties and managers
  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      const { data: groupData } = await supabase
        .from('hosp_groups')
        .select('id, name, slug, notes, created_at')
        .order('name');

      const enrichedGroups = await Promise.all((groupData || []).map(async (g) => {
        const { data: props } = await supabase
          .from('hosp_properties')
          .select('id, property_name, property_code, city, hospitality_tier, contract_status, customer_id, customers(customer_name)')
          .eq('group_id', g.id)
          .order('property_name');

        return {
          ...g,
          properties: props || [],
          propertyCount: (props || []).length,
        };
      }));

      setGroups(enrichedGroups);
    } catch (_err) {
      console.error('Error loading groups:', _err);
    } finally {
      setGroupsLoading(false);
    }
  };

  // Load groups on mount (and on tab switches) so the Groups tab badge count
  // is correct before the tab is ever opened, not just after clicking in.
  useEffect(() => {
    if (canAccess) {
      loadGroups();
    }
  }, [canAccess, activeTab]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setSavingGroup(true);
    try {
      const slug = newGroupName.trim().toLowerCase().replace(/\s+/g, '-');
      const { error: err } = await supabase
        .from('hosp_groups')
        .insert({ name: newGroupName.trim(), slug });
      if (err) throw err;
      setNewGroupName('');
      setShowNewGroup(false);
      await loadGroups();
    } catch (err) {
      alert('Error creating group: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('Delete this group? Properties will be unlinked but not deleted.')) return;
    try {
      // Unlink properties first
      await supabase.from('hosp_properties').update({ group_id: null }).eq('group_id', groupId);
      // Delete group
      await supabase.from('hosp_groups').delete().eq('id', groupId);
      await loadGroups();
    } catch (err) {
      alert('Error deleting group: ' + (err instanceof Error ? err.message : 'Unknown'));
    }
  };

  const handleAssignPropertyToGroup = async (propertyId: string, groupId: string) => {
    try {
      await supabase.from('hosp_properties').update({ group_id: groupId }).eq('id', propertyId);
      await loadGroups();
      await loadEnrichedData();
    } catch (err) {
      alert('Error assigning property: ' + (err instanceof Error ? err.message : 'Unknown'));
    }
  };

  const handleRemovePropertyFromGroup = async (propertyId: string) => {
    try {
      await supabase.from('hosp_properties').update({ group_id: null }).eq('id', propertyId);
      await loadGroups();
      await loadEnrichedData();
    } catch (err) {
      alert('Error removing property: ' + (err instanceof Error ? err.message : 'Unknown'));
    }
  };

  // Properties not in any group (for assignment dropdown)
  const ungroupedProperties = properties.filter(p => !p.group_id);

  const handleRefresh = async () => {
    await refreshProperties();
    await loadEnrichedData();
  };

  // Re-pull the property list whenever the board is opened or the window
  // regains focus, so a freshly onboarded property shows up without needing a
  // hard refresh.
  useEffect(() => {
    refreshProperties();
    const onFocus = () => refreshProperties();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Toggle published/featured inline
  const toggleFlag = async (property: Record<string, any>, field: string) => {
    const newValue = !property[field];
    try {
      const { error: updateError } = await supabase
        .from('hosp_properties')
        .update({ [field]: newValue })
        .eq('id', property.id);
      if (updateError) throw updateError;
      setProperties(prev => prev.map(p =>
        p.id === property.id ? { ...p, [field]: newValue } : p
      ));
    } catch (_err) {
      console.error(`Error toggling ${field}:`, _err);
      return;
    }
    // Published and featured both show on the public directory, so it rebuilds at once (3 Nov 2026: unpublishing
    // here left the property listed until someone pressed Rebuild).
    try {
      await requestRebuild();
    } catch (err) {
      alert('Saved, but the directory did not rebuild: ' + (err instanceof Error ? err.message : 'Unknown error') + '. Press Rebuild directory.');
    }
  };

  /** Asks the public site to rebuild the POS directory; throws when it was not accepted. */
  const requestRebuild = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pos-website-rebuild`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({}),
      }
    );
    const result = await res.json();
    if (!result.success) throw new Error(result.error);
  };

  // Trigger POS directory rebuild
  const triggerRebuild = async () => {
    setRebuilding(true);
    try {
      await requestRebuild();
      alert('Directory rebuild triggered, changes live in about 90 seconds');
    } catch (err) {
      alert('Failed to trigger rebuild: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setRebuilding(false);
    }
  };

  // Property columns for flat list
  const propertyColumns: Column<Record<string, any>>[] = [
    {
      key: 'property_name',
      label: 'Property',
      sortable: true,
      render: (row) => (
        <div>
          <div className="fw-500">{row.property_name}</div>
          {row.property_code && (
            <div className="fs-xs text-light">
              {row.property_code}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'temperature',
      label: 'Temperature',
      sortable: true,
      align: 'center',
      render: (row) => {
        const temp = row.temperature as string | null;
        if (!temp) return <span>-</span>;
        const cfg = TEMPERATURE_CONFIG[temp as Temperature];
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: cfg?.color ?? 'var(--color-text-light)',
              }}
            />
            {cfg?.label ?? temp}
          </span>
        );
      },
    },
    {
      key: 'assured_until',
      label: 'Assured Until',
      sortable: true,
      render: (row) => row.assured_until ? formatDate(row.assured_until) : <span className="text-light">-</span>
    },
    {
      key: 'hospitality_tier',
      label: 'Tier',
      align: 'center',
      sortable: true,
      render: (row) => <TierBadge tier={row.hospitality_tier} />
    },
    {
      key: 'total_beds',
      label: 'Beds',
      align: 'center',
      sortable: true
    },
    {
      key: 'contractStatus',
      label: 'Status',
      align: 'center',
      sortable: true,
      render: (row) => {
        const config = (STATUS_CONFIG as Record<string, any>)[row.contractStatus] ?? (STATUS_CONFIG as Record<string, any>).onboarding ?? {};
        return <StatusBadge bg={config.bg} color={config.color}>{config.label}</StatusBadge>;
      }
    },
    {
      key: 'ambassadorName',
      label: 'Ambassador',
      sortable: true,
      render: (row) => row.ambassadorName
        ? <span className="text-sm">{row.ambassadorName}</span>
        : <span className="text-light">-</span>
    },
    {
      key: 'managerName',
      label: 'User',
      sortable: true,
      render: (row) => row.managerName || <span className="text-light">-</span>
    },
    {
      key: 'is_published',
      label: 'Listed',
      align: 'center',
      sortable: true,
      render: (row) => (
        <input
          type="checkbox"
          checked={!!row.is_published}
          disabled={!canEditBoard}
          onChange={(e) => { e.stopPropagation(); toggleFlag(row, 'is_published'); }}
          onClick={(e) => e.stopPropagation()}
          title={row.is_published ? 'Published' : 'Not published'}
          style={{ cursor: canEditBoard ? 'pointer' : 'not-allowed', width: 16, height: 16 }}
        />
      )
    },
    {
      key: 'is_featured',
      label: 'Featured',
      align: 'center',
      sortable: true,
      render: (row) => (
        <input
          type="checkbox"
          checked={!!row.is_featured}
          disabled={!canEditBoard}
          onChange={(e) => { e.stopPropagation(); toggleFlag(row, 'is_featured'); }}
          onClick={(e) => e.stopPropagation()}
          title={row.is_featured ? 'Featured' : 'Not featured'}
          style={{ cursor: canEditBoard ? 'pointer' : 'not-allowed', width: 16, height: 16 }}
        />
      )
    }
  ];

  // Dynamic filters built from current data
  const uniqueAmbassadors = [...new Set(properties.map(p => p.ambassadorName).filter(Boolean))].sort();
  const uniqueUsers = [...new Set(properties.map(p => p.managerName).filter(Boolean))].sort();

  const propertyFilters = [
    {
      key: 'contractStatus',
      label: 'Status',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'active', label: 'Active' },
        { value: 'onboarding', label: 'Onboarding' },
        { value: 'expired', label: 'Expired' },
      ],
    },
    {
      key: 'temperature',
      label: 'Temperature',
      options: [
        { value: 'all', label: 'All' },
        { value: 'green', label: 'Green' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'red', label: 'Red' },
      ],
    },
    {
      key: 'hospitality_tier',
      label: 'Tier',
      options: [
        { value: 'all', label: 'All Tiers' },
        { value: 'assured', label: 'Assured' },
        { value: 'distinguished', label: 'Distinguished' },
        { value: 'signature', label: 'Signature' },
      ],
    },
    ...(uniqueAmbassadors.length > 0 ? [{
      key: 'ambassadorName',
      label: 'Ambassador',
      options: [
        { value: 'all', label: 'All Ambassadors' },
        ...uniqueAmbassadors.map(a => ({ value: a, label: a })),
      ],
    }] : []),
    ...(uniqueUsers.length > 0 ? [{
      key: 'managerName',
      label: 'User',
      options: [
        { value: 'all', label: 'All Users' },
        ...uniqueUsers.map(u => ({ value: u, label: u })),
      ],
    }] : []),
  ];

  // Board toolbar: filter config (manager / ambassador / tier) reusing the
  // unique lists already built for the list view, and sort options.
  const boardFilterConfig = [
    { key: 'managerName', label: 'Manager', options: [{ value: 'all', label: 'All' }, ...uniqueUsers.map((u) => ({ value: u, label: u }))] },
    { key: 'ambassadorName', label: 'Ambassador', options: [{ value: 'all', label: 'All' }, ...uniqueAmbassadors.map((a) => ({ value: a, label: a }))] },
    { key: 'hospitality_tier', label: 'Tier', options: [
      { value: 'all', label: 'All' },
      { value: 'assured', label: 'Assured' },
      { value: 'distinguished', label: 'Distinguished' },
      { value: 'signature', label: 'Signature' },
    ] },
  ];
  const boardSortOptions = [
    { value: 'expiry', label: 'Soonest to expire' },
    { value: 'name', label: 'Name (A–Z)' },
  ];
  const effectiveSort = boardSort || (viewMode === 'renewals' ? 'expiry' : 'name');

  // Apply the board search/filters/sort to the active category's rows; the
  // boards render whatever order they're given.
  const boardProperties = useMemo(() => {
    const source = activeTab === 'community-living' ? communityProperties : hospitalityProperties;
    const q = boardSearch.trim().toLowerCase();
    const rows = source.filter((p) => {
      const row = p as Record<string, unknown>;
      if (q) {
        const name = String(row.property_name ?? '').toLowerCase();
        const code = String(row.property_code ?? '').toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      for (const key of BOARD_FILTER_KEYS) {
        const val = boardFilters[key];
        if (val && val !== 'all' && String(row[key] ?? '') !== val) return false;
      }
      return true;
    });
    return [...rows].sort((a, b) => {
      const ra = a as Record<string, unknown>;
      const rb = b as Record<string, unknown>;
      if (effectiveSort === 'expiry') {
        const av = String(ra.assured_until ?? '9999-12-31');
        const bv = String(rb.assured_until ?? '9999-12-31');
        return av < bv ? -1 : av > bv ? 1 : 0;
      }
      return String(ra.property_name ?? '').localeCompare(String(rb.property_name ?? ''));
    });
  }, [activeTab, communityProperties, hospitalityProperties, boardSearch, boardFilters, effectiveSort]);

  // Permission check
  if (permissionLoading) return <LoadingSpinner />;
  if (!canAccess) return <AccessDenied feature="pos_properties" />;
  if (error) return <ErrorDisplay error={error} onRetry={loadEnrichedData} />;

  return (
    <div className="pos-themed">
      {/* View toggle — own page-tabs row, left-aligned, matches the
          Proposals + ServiceLogs + POS Certificates pattern. Only
          meaningful on the property category tabs (Groups has its own
          view). */}
      {!selectedProperty && (activeTab === 'hospitality' || activeTab === 'community-living') && (
        <div className="page-tabs">
          <button
            className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
          <button
            className={`page-tab ${viewMode === 'board' ? 'active' : ''}`}
            onClick={() => setViewMode('board')}
          >
            Onboarding
          </button>
          <button
            className={`page-tab ${viewMode === 'renewals' ? 'active' : ''}`}
            onClick={() => setViewMode('renewals')}
          >
            Renewals
          </button>
        </div>
      )}

      {/* Category tabs — only show when not drilled into a property */}
      {!selectedProperty && (
        <div className="page-tabs">
          <button className={`page-tab ${activeTab === 'hospitality' ? 'active' : ''}`} onClick={() => setActiveTab('hospitality')}>
            Hospitality <span className="tab-badge">{hospitalityProperties.length}</span>
          </button>
          <button className={`page-tab ${activeTab === 'community-living' ? 'active' : ''}`} onClick={() => setActiveTab('community-living')}>
            Community Living <span className="tab-badge">{communityProperties.length}</span>
          </button>
          <button className={`page-tab ${activeTab === 'groups' ? 'active' : ''}`} onClick={() => setActiveTab('groups')}>
            Groups <span className="tab-badge secondary">{groups.length}</span>
          </button>
        </div>
      )}

      {/* Board toolbar — search / filter / sort for the Onboarding + Renewals
          boards (the List view has its own table controls). */}
      {(activeTab === 'hospitality' || activeTab === 'community-living') && (viewMode === 'board' || viewMode === 'renewals') && !selectedProperty && (
        <BoardToolbar
          search={boardSearch}
          onSearchChange={setBoardSearch}
          searchPlaceholder="Search property name or code..."
          filters={boardFilterConfig}
          filterValues={boardFilters}
          onFilterChange={(key, value) => setBoardFilters((prev) => ({ ...prev, [key]: value }))}
          sortOptions={boardSortOptions}
          sortValue={effectiveSort}
          onSortChange={setBoardSort}
        />
      )}

      {/* Properties — Onboarding Board (default) */}
      {(activeTab === 'hospitality' || activeTab === 'community-living') && viewMode === 'board' && !selectedProperty && (
        <PortfolioBoard
          supabase={supabase}
          properties={boardProperties}
          openProperty={openPropertyFromBoard}
          onRefresh={handleRefresh}
          canEdit={canEditBoard}
        />
      )}

      {/* Properties — Renewals Board (sales kanban for certified properties) */}
      {(activeTab === 'hospitality' || activeTab === 'community-living') && viewMode === 'renewals' && !selectedProperty && (
        <RenewalsBoard
          supabase={supabase}
          properties={boardProperties}
          openProperty={(id) => openPropertyFromBoard(id)}
          onRefresh={handleRefresh}
          canEdit={canEditBoard}
        />
      )}

      {/* Properties — List View */}
      {(activeTab === 'hospitality' || activeTab === 'community-living') && viewMode === 'list' && !selectedProperty && (
        <DataTable
          columns={propertyColumns}
          data={activeTab === 'community-living' ? communityProperties : hospitalityProperties}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search properties..."
          searchKeys={['property_name', 'property_code', 'customerName', 'city', 'ambassadorName']}
          filters={propertyFilters}
          defaultSort={{ key: 'property_name', direction: 'asc' }}
          headerActions={({
            primary: canCreate ? { label: '+ Property', onClick: () => setDetailModal({ id: null, mode: 'create' }), icon: '🛏️' } : undefined,
            secondary: { label: rebuilding ? 'Rebuilding...' : 'Update Directory', onClick: triggerRebuild, disabled: rebuilding, icon: '🔄' },
            onSync: handleRefresh
          } as HeaderActions)}
          onRowClick={(row) => setDetailModal({ id: row.id, mode: 'view' })}
          actions={(row) => (
            <>
              <span
                className="action-icon"
                onClick={(e) => { e.stopPropagation(); setDetailModal({ id: row.id, mode: 'view' }); }}
                title="View property"
              >
                👁️
              </span>
              {canEditProp(row) && (
                <span
                  className="action-icon"
                  onClick={(e) => { e.stopPropagation(); setDetailModal({ id: row.id, mode: 'edit' }); }}
                  title="Edit property"
                >
                  ✏️
                </span>
              )}
              <span
                className="action-icon"
                onClick={(e) => { e.stopPropagation(); setSelectedProperty(row); }}
                title="Manage rooms"
              >
                🛏️
              </span>
              {(() => {
                const dirUrl = getDirectoryUrl(row);
                return dirUrl ? (
                  <a
                    href={dirUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-icon"
                    onClick={(e) => e.stopPropagation()}
                    title="View published listing"
                    style={{ textDecoration: 'none' }}
                  >
                    🌐
                  </a>
                ) : (
                  <span
                    className="action-icon"
                    style={{ opacity: 0.25, cursor: 'default' }}
                    title="Not published"
                    onClick={(e) => e.stopPropagation()}
                  >
                    🌐
                  </span>
                );
              })()}
            </>
          )}
          pageSize={25}
          emptyMessage="No properties found. Add a property to get started."
        />
      )}

      {/* Groups Tab */}
      {activeTab === 'groups' && !selectedProperty && (
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-lg">
            <p className="text-sm text-muted m-0">
              Organize properties into groups for portfolio management
            </p>
            {canCreate && (
              <button className="pos-btn pos-btn-primary" onClick={() => setShowNewGroup(true)}>
                + New Group
              </button>
            )}
          </div>

          {/* New Group Modal */}
          {showNewGroup && (
            <div className="modal-overlay" onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                <div className="modal-header">
                  <h2 className="modal-title">New Group</h2>
                  <button className="modal-close" onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}>&times;</button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="pos-label">Group Name</label>
                    <input
                      type="text"
                      className="pos-input"
                      placeholder="e.g. Tintswalo Collection"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="pos-btn pos-btn-secondary" onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}>
                    Cancel
                  </button>
                  <button className="pos-btn pos-btn-primary" onClick={handleCreateGroup} disabled={savingGroup || !newGroupName.trim()}>
                    {savingGroup ? 'Creating...' : 'Create Group'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Groups List */}
          {groupsLoading ? (
            <LoadingSpinner />
          ) : groups.length === 0 ? (
            <div className="pos-empty-state">
              <div className="pos-empty-state-icon">🏨</div>
              <div className="pos-empty-state-title">No Groups</div>
              <p className="pos-empty-state-text">
                Create a group to organize properties into portfolios. Groups let you manage multiple properties under one umbrella.
              </p>
            </div>
          ) : (
            <DataTable
              data={groups}
              columns={[
                { key: 'name', label: 'Group', render: (row) => <div className="fw-500">{row.name}</div> },
                { key: 'propertyCount', label: 'Properties', render: (row) => row.propertyCount || 0 },
                { key: 'managers', label: 'Managers', render: (row) => row.managers?.length > 0 ? row.managers.map((m: Record<string, any>) => `${m.users?.first_name || ''} ${m.users?.last_name || ''}`).join(', ') : '-' },
                { key: 'created_at', label: 'Created', render: (row) => row.created_at ? formatDate(row.created_at) : '-' },
              ]}
              searchKeys={['name']}
              emptyMessage="No groups yet. Create a group to organize properties into portfolios."
              defaultSort={{ key: 'name', direction: 'asc' }}
              actions={(row) => (
                <>
                  <span className="action-icon" onClick={() => setViewingGroup(row)} title="View group">👁️</span>
                  <span className="action-icon" onClick={() => setEditingGroup(row)} title="Edit group">✏️</span>
                </>
              )}
            />

          )}
        </div>
      )}

      {/* Group View Modal (read-only) */}
      {viewingGroup && (
        <div className="modal-overlay" onClick={() => setViewingGroup(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{viewingGroup.name}</h2>
              <button className="modal-close" onClick={() => setViewingGroup(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="view-section">
                <h3 className="section-title">Properties ({viewingGroup.propertyCount})</h3>
                {viewingGroup.properties?.length > 0 ? (
                  <div style={{ fontSize: 'var(--text-base)' }}>
                    {viewingGroup.properties.map((prop: Record<string, any>) => {
                      const statusCfg = (STATUS_CONFIG as Record<string, any>)[prop.contract_status] ?? (STATUS_CONFIG as Record<string, any>).onboarding ?? {};
                      return (
                        <div key={prop.id} className="detail-row" style={{ padding: '0.4rem 0', cursor: 'pointer' }} onClick={() => { setViewingGroup(null); setDetailModal({ id: prop.id, mode: 'view' }); }}>
                          <span className="label" style={{ color: POS_GOLD }}>{prop.property_name}</span>
                          <span className="value"><StatusBadge bg={statusCfg.bg} color={statusCfg.color}>{statusCfg.label}</StatusBadge></span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-muted">No properties</div>
                )}
              </div>

              {viewingGroup.notes && (
                <div className="view-section">
                  <h3 className="section-title">Notes</h3>
                  <div className="text-sm">{viewingGroup.notes}</div>
                </div>
              )}

              {viewingGroup.managers?.length > 0 && (
                <div className="view-section">
                  <h3 className="section-title">Managers</h3>
                  <div className="text-sm">{viewingGroup.managers.map((m: Record<string, any>) => `${m.users?.first_name || ''} ${m.users?.last_name || ''}`).join(', ')}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setViewingGroup(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Group Edit Modal */}
      {editingGroup && (
        <div className="modal-overlay" onClick={() => setEditingGroup(null)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Group</h2>
              <button className="modal-close" onClick={() => setEditingGroup(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-section">
                <div className="modal-grid-2">
                  <div style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Group Name</label>
                    <input className="form-input" value={editingGroup.name || ''} onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={2} value={editingGroup.notes || ''} onChange={(e) => setEditingGroup({ ...editingGroup, notes: e.target.value })} placeholder="Internal notes..." style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="modal-section-heading">Properties ({editingGroup.propertyCount})</h3>
                {editingGroup.properties?.length > 0 && (
                  <div style={{ overflowX: 'auto', marginBottom: '0.5rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-base)' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600, color: 'var(--color-text-light)' }}>Property</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600, color: 'var(--color-text-light)' }}>Location</th>
                          <th style={{ textAlign: 'center', padding: '0.5rem', fontWeight: 600, color: 'var(--color-text-light)' }}>Tier</th>
                          <th style={{ width: 48 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editingGroup.properties.map((prop: Record<string, any>) => (
                          <tr key={prop.id} style={{ borderBottom: '1px solid var(--color-border-light, #f3f4f6)' }}>
                            <td style={{ padding: '0.5rem' }} className="fw-500">{prop.property_name}</td>
                            <td style={{ padding: '0.5rem' }}>{prop.city || '-'}</td>
                            <td style={{ textAlign: 'center', padding: '0.5rem' }}><TierBadge tier={prop.hospitality_tier} /></td>
                            <td style={{ padding: '0.5rem' }}>
                              <span className="action-icon text-error" onClick={async () => { await handleRemovePropertyFromGroup(prop.id); const updated = groups.find(g => g.id === editingGroup.id); if (updated) setEditingGroup(updated); }} title="Remove from group">✕</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <SearchSelect
                  placeholder="+ Add property..."
                  options={ungroupedProperties.map(p => ({ value: p.id, label: p.property_name }))}
                  value={null}
                  onChange={async (v) => { if (v) { await handleAssignPropertyToGroup(v, editingGroup.id); const updated = groups.find(g => g.id === editingGroup.id); if (updated) setEditingGroup(updated); } }}
                />
              </div>
            </div>
            <div className="modal-footer justify-between">
              <div>
                {canDeleteGlobal && (
                  <button type="button" onClick={async () => { await handleDeleteGroup(editingGroup.id); setEditingGroup(null); }} style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: 'var(--text-base)' }}>
                    Delete Group
                  </button>
                )}
              </div>
              <div className="btn-grid">
                <button className="btn btn-secondary" onClick={() => setEditingGroup(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    const slug = (editingGroup.name || '').trim().toLowerCase().replace(/\s+/g, '-');
                    await supabase.from('hosp_groups').update({ name: editingGroup.name, notes: editingGroup.notes, slug }).eq('id', editingGroup.id);
                    await loadGroups();
                    setEditingGroup(null);
                  } catch (err) {
                    alert('Error saving: ' + (err instanceof Error ? err.message : 'Unknown'));
                  }
                }}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level 2: Rooms Page (full width) */}
      {selectedProperty && (
        <PropertyRoomsView
          property={selectedProperty}
          onBack={() => setSelectedProperty(null)}
          onEdit={() => setDetailModal({ id: selectedProperty.id, mode: 'edit' })}
          onManage={() => setDetailModal({ id: selectedProperty.id, mode: 'view' })}
          canEdit={canEditProp(selectedProperty)}
          supabase={supabase}
          onRefresh={handleRefresh}
          setCertificatesContext={setCertificatesContext}
          setBookingsContext={setBookingsContext}
          setServicesContext={setServicesContext}
        />
      )}

      {/* Unified Property Detail Modal — must render BEFORE the
          sub-context modals below so that clicking 📜/📅/🔧 on a room
          row opens the relevant sub-modal *on top of* this modal
          (siblings share z-index; last-in-DOM wins paint order). */}
      {detailModal && (
        <PropertyDetailModal
          propertyId={detailModal.id}
          mode={detailModal.mode}
          initialCustomerId={detailModal.initialCustomerId}
          initialTab={detailModal.initialTab}
          onClose={() => setDetailModal(null)}
          onRefresh={handleRefresh}
          setCertificatesContext={setCertificatesContext}
          setBookingsContext={setBookingsContext}
          setServicesContext={setServicesContext}
        />
      )}

      {/* Certificates / Bookings / Services sub-modals — rendered AFTER
          PropertyDetailModal so they stack above it when triggered from
          a room action button inside it. */}
      {certificatesContext && (
        <CertificatesModal
          context={certificatesContext as any}
          onClose={() => setCertificatesContext(null)}
          supabase={supabase}
        />
      )}
      {bookingsContext && (
        <BookingsModal
          context={bookingsContext as any}
          onClose={() => setBookingsContext(null)}
          supabase={supabase}
        />
      )}
      {servicesContext && (
        <ServicesModal
          context={servicesContext as any}
          onClose={() => setServicesContext(null)}
          supabase={supabase}
        />
      )}
    </div>
  );
}
